import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { usePaywall } from '@/hooks/use-paywall';
import { FeatureFlag, PLAN_ENTITLEMENTS, upgradeTierFor, PlanLimit } from '@fluenceur/types';
import { useCallback } from 'react';

/**
 * What the brand's plan keeps closed in the dashboard, and the upsell that
 * opens on it. Flows reads the FLOWS flag (an organisation override still
 * wins), connected assistants the plan's entitlements. Nothing is locked while
 * the plan is still loading: the server refuses anyway, and a menu that flashes
 * an upsell at a paying brand is worse than a 402 answered by the same upsell.
 */
export function usePlanGates() {
  const { isSuccess, planTier, hasFeature, getLimit, getLimitUsage } = usePlanFeaturesQuery();
  const { openPlanLimitModal } = usePaywall();

  const openUpsell = useCallback(
    (limit: PlanLimit, used?: number) =>
      openPlanLimitModal({
        limit,
        currentTier: planTier,
        targetTier: upgradeTierFor(limit, planTier, used),
        ...(limit === PlanLimit.BRIEFS
          ? { used: used ?? getLimitUsage('maxActiveBriefs'), max: getLimit('maxActiveBriefs') }
          : {}),
        ...(limit === PlanLimit.FLOWS && used !== undefined
          ? { used, max: getLimit('maxFlows') }
          : {}),
      }),
    [openPlanLimitModal, planTier, getLimit, getLimitUsage]
  );

  return {
    planTier,
    briefsAtLimit:
      isSuccess &&
      getLimit('maxActiveBriefs') !== -1 &&
      getLimitUsage('maxActiveBriefs') >= getLimit('maxActiveBriefs'),
    flowsLocked: isSuccess && !hasFeature(FeatureFlag.FLOWS),
    mcpLocked: isSuccess && !PLAN_ENTITLEMENTS[planTier].mcp,
    openUpsell,
  };
}
